import { getDatabase, ref, get } from 'firebase/database';
import segmentData from '../data/segment.json';

export async function loadCustomerDashboard(customerId) {
  try {
    const db = getDatabase();

    // Load customer name
    const customerRef = ref(db, `customers/${customerId}`);
    const customerSnap = await get(customerRef);
    const customerName = customerSnap.val()?.name || '';

    // Load all departments to get all workshops
    const departmentsRef = ref(db, `customers/${customerId}/departments`);
    const departmentsSnap = await get(departmentsRef);
    const departments = departmentsSnap.val() || {};
    console.log('Departments:', departments);

    // Initialize data structures
    const qualityAspectMap = new Map();
    const departmentStats = [];
    const projectTimeline = [];
    let totalTasks = 0;
    let completedTasks = 0;
    let inProgressTasks = 0;
    let notStartedTasks = 0;

    // Convert Maps to arrays and calculate averages
    const processQualityAspects = (aspectsMap) => {
      console.log('Processing quality aspects map:', 
        Array.from(aspectsMap.entries()).map(([id, aspect]) => ({
          id,
          ...aspect,
          departmentSet: aspect.departmentSet ? Array.from(aspect.departmentSet) : undefined,
          projectSet: aspect.projectSet ? Array.from(aspect.projectSet) : undefined
        }))
      );
      
      // Convert Map to array and sort by potential
      const aspects = Array.from(aspectsMap.values())
        .map(aspect => ({
          id: aspect.id,
          name: aspect.name,
          category: aspect.category,
          voteWhereWantToBe: aspect.voteWhereWantToBe,
          voteWhereWeAre: aspect.voteWhereWeAre,
          potential: aspect.potential,
          count: aspect.count,
          workshopCount: aspect.workshopCount || 1,
          departmentCount: aspect.departmentSet?.size || 1,
          projectCount: aspect.projectSet?.size || 1
        }))
        .filter(aspect => 
          typeof aspect.voteWhereWantToBe === 'number' && 
          typeof aspect.voteWhereWeAre === 'number' &&
          typeof aspect.potential === 'number'
        )
        .sort((a, b) => b.potential - a.potential)
        .slice(0, 5);
      
      console.log('Processed quality aspects:', aspects);
      return aspects;
    };

    // Process all departments
    for (const [deptId, dept] of Object.entries(departments)) {
      console.log('Processing department:', deptId, dept);
      
      const deptStat = {
        id: deptId,
        name: dept.name,
        projects: [],
        completedWorkshops: 0,
        totalTasks: 0,
        completedTasks: 0,
        qualityAspects: new Map()
      };

      // Process all projects in department
      if (dept.projects) {
        for (const [projectId, project] of Object.entries(dept.projects)) {
          console.log('Processing project:', projectId, project);
          
          const projectStat = {
            id: projectId,
            name: project.name,
            department: dept.name,
            startDate: project.startDate || new Date().toISOString(),
            status: 'In Progress',
            completedWorkshops: 0,
            totalWorkshops: 0,
            qualityAspects: new Map(),
            workshops: project.workshops || {}
          };

          // Process all workshops in project
          if (project.workshops) {
            console.log('Project workshops:', project.workshops);
            
            // Handle both array and object workshop storage
            const workshopEntries = Array.isArray(project.workshops) 
              ? project.workshops.map(id => [id, {}])
              : Object.entries(project.workshops);
            
            // Count total workshops
            projectStat.totalWorkshops = workshopEntries.length;
            console.log('Total workshops:', projectStat.totalWorkshops);

            // Process each workshop
            for (const [workshopId] of workshopEntries) {
              console.log('Processing workshop:', workshopId);
              
              // Load workshop data
              let workshopRef;
              if (deptId && projectId) {
                workshopRef = ref(db, `customers/${customerId}/departments/${deptId}/projects/${projectId}/workshops/${workshopId}`);
              } else {
                workshopRef = ref(db, `customers/${customerId}/workshop/${workshopId}`);
              }
              const workshopSnap = await get(workshopRef);
              const workshopData = workshopSnap.val() || {};
              console.log('Workshop data:', workshopData);
              
              // Mark workshop as completed if it exists
              if (workshopData && Object.keys(workshopData).length > 0) {
                projectStat.completedWorkshops++;
                deptStat.completedWorkshops++;
                console.log('Marked workshop as completed:', workshopId);
              }
              
              // Process workshop results
              const results = [];
              
              // Check for completed results
              if (workshopData.results) {
                console.log('Found results for workshop:', workshopId, workshopData.results);
                for (const [segmentId, result] of Object.entries(workshopData.results)) {
                  if (result) {
                    // Get votes from either the root or the votes object
                    const voteWhereWantToBe = Number(result.voteWhereWantToBe);
                    const voteWhereWeAre = Number(result.voteWhereWeAre);
                    console.log('Processing result:', { segmentId, voteWhereWantToBe, voteWhereWeAre });
                    
                    if (!isNaN(voteWhereWantToBe) && !isNaN(voteWhereWeAre)) {
                      // Find segment info
                      for (const category of segmentData) {
                        for (const subcategory of category.children) {
                          if (subcategory.id === segmentId) {
                            const potential = voteWhereWantToBe - voteWhereWeAre;
                            results.push({
                              id: segmentId,
                              name: subcategory.text.en.name,
                              category: category.text.en.name,
                              voteWhereWantToBe,
                              voteWhereWeAre,
                              potential
                            });
                            console.log('Added result:', results[results.length - 1]);
                            break;
                          }
                        }
                      }
                    }
                  }
                }
              }
              
              // Then check for active voting results only if no completed results
              if (results.length === 0 && workshopData.activeVoting?.votes) {
                const votes = Object.values(workshopData.activeVoting.votes);
                const segmentId = workshopData.activeVoting.segmentId;
                
                if (segmentId && votes.length > 0) {
                  let totalWantToBe = 0;
                  let totalWeAre = 0;
                  let count = 0;
                  
                  for (const vote of votes) {
                    if (typeof vote.voteWhereWantToBe === 'number' && typeof vote.voteWhereWeAre === 'number') {
                      totalWantToBe += vote.voteWhereWantToBe;
                      totalWeAre += vote.voteWhereWeAre;
                      count++;
                    }
                  }
                  
                  if (count > 0) {
                    const voteWhereWantToBe = Math.round(totalWantToBe / count);
                    const voteWhereWeAre = Math.round(totalWeAre / count);
                    const potential = voteWhereWantToBe - voteWhereWeAre;
                    
                    for (const category of segmentData) {
                      for (const subcategory of category.children) {
                        if (subcategory.id === segmentId) {
                          results.push({
                            id: segmentId,
                            name: subcategory.text.en.name,
                            category: category.text.en.name,
                            voteWhereWantToBe,
                            voteWhereWeAre,
                            potential
                          });
                          break;
                        }
                      }
                    }
                  }
                }
              }

              // Update statistics if we have results
              if (results.length > 0) {
                console.log('Updating quality aspects with results:', results);
                
                // Update quality aspects at all levels
                results.forEach(result => {
                  // Update project level
                  if (!projectStat.qualityAspects.has(result.id)) {
                    projectStat.qualityAspects.set(result.id, {
                      id: result.id,
                      name: result.name,
                      category: result.category,
                      voteWhereWantToBe: result.voteWhereWantToBe,
                      voteWhereWeAre: result.voteWhereWeAre,
                      potential: result.potential,
                      count: 0,
                      workshopCount: 0,
                      departmentSet: new Set(),
                      projectSet: new Set()
                    });
                    console.log('Created new project quality aspect:', result.id);
                  }
                  const projectAspect = projectStat.qualityAspects.get(result.id);
                  projectAspect.count++;
                  projectAspect.workshopCount++;
                  projectAspect.departmentSet.add(deptId);
                  projectAspect.projectSet.add(projectId);
                  console.log('Updated project quality aspect:', {
                    ...projectAspect,
                    departmentSet: Array.from(projectAspect.departmentSet),
                    projectSet: Array.from(projectAspect.projectSet)
                  });
                  
                  // Update department level
                  if (!deptStat.qualityAspects.has(result.id)) {
                    deptStat.qualityAspects.set(result.id, {
                      id: result.id,
                      name: result.name,
                      category: result.category,
                      voteWhereWantToBe: result.voteWhereWantToBe,
                      voteWhereWeAre: result.voteWhereWeAre,
                      potential: result.potential,
                      count: 0,
                      workshopCount: 0,
                      departmentSet: new Set(),
                      projectSet: new Set()
                    });
                    console.log('Created new department quality aspect:', result.id);
                  }
                  const deptAspect = deptStat.qualityAspects.get(result.id);
                  deptAspect.count++;
                  deptAspect.workshopCount++;
                  deptAspect.departmentSet.add(deptId);
                  deptAspect.projectSet.add(projectId);
                  console.log('Updated department quality aspect:', {
                    ...deptAspect,
                    departmentSet: Array.from(deptAspect.departmentSet),
                    projectSet: Array.from(deptAspect.projectSet)
                  });
                  
                  // Update global level
                  if (!qualityAspectMap.has(result.id)) {
                    qualityAspectMap.set(result.id, {
                      id: result.id,
                      name: result.name,
                      category: result.category,
                      voteWhereWantToBe: result.voteWhereWantToBe,
                      voteWhereWeAre: result.voteWhereWeAre,
                      potential: result.potential,
                      count: 0,
                      workshopCount: 0,
                      departmentSet: new Set(),
                      projectSet: new Set()
                    });
                    console.log('Created new global quality aspect:', result.id);
                  }
                  const globalAspect = qualityAspectMap.get(result.id);
                  globalAspect.count++;
                  globalAspect.workshopCount++;
                  globalAspect.departmentSet.add(deptId);
                  globalAspect.projectSet.add(projectId);
                  console.log('Updated global quality aspect:', {
                    ...globalAspect,
                    departmentSet: Array.from(globalAspect.departmentSet),
                    projectSet: Array.from(globalAspect.projectSet)
                  });
                });
              }

              // Process tasks
              if (workshopData.tasks) {
                const tasks = Object.values(workshopData.tasks);
                const workshopTotalTasks = tasks.length;
                let workshopCompletedTasks = 0;
                let workshopInProgressTasks = 0;
                let workshopNotStartedTasks = 0;
                tasks.forEach(t => {
                  const status = (t.status || 'Not Started').toLowerCase();
                  if (status === 'completed') workshopCompletedTasks++;
                  else if (status === 'in progress') workshopInProgressTasks++;
                  else if (status === 'not started') workshopNotStartedTasks++;
                });
                deptStat.totalTasks += workshopTotalTasks;
                deptStat.completedTasks += workshopCompletedTasks;
                totalTasks += workshopTotalTasks;
                completedTasks += workshopCompletedTasks;
                inProgressTasks += workshopInProgressTasks;
                notStartedTasks += workshopNotStartedTasks;
              }
            }
          }

          // Calculate project quality aspect averages and get top 5
          projectStat.topQualityAspects = processQualityAspects(projectStat.qualityAspects);

          deptStat.projects.push(projectStat);
          projectTimeline.push(projectStat);
        }
      }

      departmentStats.push(deptStat);
    }

    // Process final results
    const results = {
      customerName,
      topQualityAspects: processQualityAspects(qualityAspectMap),
      taskStats: {
        total: totalTasks,
        completed: completedTasks,
        inProgress: inProgressTasks,
        notStarted: notStartedTasks,
        completionRate: totalTasks ? Math.round((completedTasks / totalTasks) * 100) : 0
      },
      departmentStats: [],
      projectTimeline: []
    };

    console.log('Final quality aspects:', results.topQualityAspects);
    
    // Add department stats
    for (const [deptId, deptStat] of departmentStats.entries()) {
      results.departmentStats.push({
        id: deptId,
        name: deptStat.name,
        projectCount: deptStat.projects.length,
        completedWorkshops: deptStat.completedWorkshops,
        totalTasks: deptStat.totalTasks,
        completedTasks: deptStat.completedTasks,
        taskCompletionRate: deptStat.totalTasks ? Math.round((deptStat.completedTasks / deptStat.totalTasks) * 100) : 0,
        topQualityAspects: processQualityAspects(deptStat.qualityAspects),
        projects: deptStat.projects
      });
      console.log('Added department stats:', deptId, results.departmentStats[results.departmentStats.length - 1]);
    }
    
    // Add project timeline
    for (const [projectId, projectStat] of projectTimeline.entries()) {
      results.projectTimeline.push({
        id: projectId,
        name: projectStat.name,
        department: projectStat.department,
        startDate: projectStat.startDate || new Date().toISOString(),
        status: projectStat.status || 'In Progress',
        totalWorkshops: projectStat.totalWorkshops,
        completedWorkshops: projectStat.completedWorkshops,
        topQualityAspects: processQualityAspects(projectStat.qualityAspects)
      });
      console.log('Added project stats:', projectId, results.projectTimeline[results.projectTimeline.length - 1]);
    }
    
    // Sort project timeline by date
    results.projectTimeline.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
    
    console.log('Final dashboard results:', results);
    return results;
  } catch (error) {
    console.error('Error loading dashboard data:', error);
    throw error;
  }
}
